import { useState, useEffect, useRef, useCallback } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";
import CoachingSessionsView from "../pages/shared/CoachingSessionsView";
import InvestmentObjectiveCard from "./InvestmentObjectiveCard";

/**
 * Coach Insights Panel — visible ONLY for coach (and admin read-only).
 * Two cards:
 *  1) Profil initial (Typeform/Claude intake) — read-only
 *  2) Mes notes privées coach — editable, auto-save
 */
export default function CoachInsightsPanel({ studentId, studentName, isReadOnly = false }) {
  const [intake, setIntake] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(true);
  const [intakeCollapsed, setIntakeCollapsed] = useState(true);
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSavedAt, setNoteSavedAt] = useState(null);
  const [activeTab, setActiveTab] = useState("intake"); // intake | objective | notes | sessions
  const debounceRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;

    (async () => {
      setIntakeLoading(true);
      try {
        const [intakeRes, noteRes] = await Promise.all([
          api.get(`/coach/intake/${studentId}`),
          api.get(`/coach/private-notes/${studentId}`),
        ]);
        if (cancelled) return;
        setIntake(intakeRes.data);
        setNote(noteRes.data?.content || "");
        setNoteSavedAt(noteRes.data?.updated_at || null);
      } catch (e) {
        if (!cancelled) {
          toast({ variant: "destructive", title: "Erreur de chargement" });
        }
      } finally {
        if (!cancelled) setIntakeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [studentId, toast]);

  const saveNote = useCallback(async (content) => {
    if (isReadOnly) return;
    setNoteSaving(true);
    try {
      const res = await api.put(`/coach/private-notes/${studentId}`, { content });
      setNoteSavedAt(res.data?.updated_at || new Date().toISOString());
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur de sauvegarde" });
    } finally {
      setNoteSaving(false);
    }
  }, [studentId, isReadOnly, toast]);

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNote(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveNote(value), 1000);
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="glass-card rounded-2xl overflow-hidden" data-testid="coach-insights-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="icon-circle icon-circle--ink">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ fontFamily: "var(--serif)", color: "var(--ink)" }}>
              Espace Coach — Confidentiel
            </h3>
            <p className="text-xs italic" style={{ color: "var(--ink-soft)", fontFamily: "var(--serif)" }}>
              {studentName ? `${studentName} · ` : ""}Non visible par l'élève
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 py-3 border-b border-line bg-paper-2 flex gap-2 flex-wrap">
        <button
          data-testid="coach-insights-tab-intake"
          onClick={() => setActiveTab("intake")}
          className={`tab-button ${activeTab === "intake" ? "tab-button-active" : ""}`}
        >
          Profil initial
        </button>
        <button
          data-testid="coach-insights-tab-objective"
          onClick={() => setActiveTab("objective")}
          className={`tab-button ${activeTab === "objective" ? "tab-button-active" : ""}`}
        >
          Objectif
        </button>
        <button
          data-testid="coach-insights-tab-notes"
          onClick={() => setActiveTab("notes")}
          className={`tab-button ${activeTab === "notes" ? "tab-button-active" : ""}`}
        >
          Mes notes privées
        </button>
        <button
          data-testid="coach-insights-tab-sessions"
          onClick={() => setActiveTab("sessions")}
          className={`tab-button ${activeTab === "sessions" ? "tab-button-active" : ""}`}
        >
          Sessions (Fathom)
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* ===== INTAKE TAB ===== */}
        {activeTab === "intake" && (
          <div data-testid="coach-insights-intake">
            {intakeLoading ? (
              <p className="text-sm italic" style={{ color: "var(--muted)" }}>Chargement…</p>
            ) : !intake?.exists ? (
              <div className="text-center py-8">
                <div className="icon-circle mx-auto mb-3" style={{ background: "var(--paper-2)" }}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "var(--muted)" }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm font-medium" style={{ color: "var(--ink)" }}>Aucun profil initial reçu</p>
                <p className="text-xs italic mt-1" style={{ color: "var(--muted)", fontFamily: "var(--serif)" }}>
                  Le profil Typeform/Claude apparaîtra ici dès qu'il sera reçu.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Header line : date + toggle pli/déplie */}
                <div className="flex items-center justify-between gap-3 pb-3 border-b border-line">
                  {intake.received_at ? (
                    <p className="text-xs italic" style={{ color: "var(--muted)", fontFamily: "var(--serif)" }}>
                      Reçu le {formatDate(intake.received_at)}
                    </p>
                  ) : <span />}
                  <button
                    data-testid="intake-toggle-collapse"
                    type="button"
                    onClick={() => setIntakeCollapsed(c => !c)}
                    className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
                    style={{
                      color: "var(--gold-dark)",
                      letterSpacing: "0.08em",
                      background: "var(--paper-2)",
                      border: "1px solid var(--gold-soft)",
                    }}
                  >
                    {intakeCollapsed ? "Déplier" : "Replier"}
                    <svg className="w-3 h-3 transition-transform" style={{ transform: intakeCollapsed ? "rotate(0deg)" : "rotate(180deg)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {!intakeCollapsed && (
                  <>
                    {intake.synthese && (
                      <section>
                        <h4 className="text-sm font-bold mb-2 uppercase tracking-wider"
                            style={{ color: "var(--gold-dark)", letterSpacing: "0.1em" }}>
                          Pré-diagnostic
                        </h4>
                        <div
                          className="intake-html"
                          data-testid="intake-synthese"
                          dangerouslySetInnerHTML={{ __html: intake.synthese }}
                        />
                      </section>
                    )}

                    {intake.conseils && (
                      <section>
                        <h4 className="text-sm font-bold mb-2 uppercase tracking-wider"
                            style={{ color: "var(--gold-dark)", letterSpacing: "0.1em" }}>
                          Pour le coach
                        </h4>
                        <div
                          className="intake-html"
                          data-testid="intake-conseils"
                          dangerouslySetInnerHTML={{ __html: intake.conseils }}
                        />
                      </section>
                    )}

                    {intake.recap && (
                      <section>
                        <details className="group">
                          <summary className="cursor-pointer text-sm font-bold uppercase tracking-wider hover:underline"
                                   style={{ color: "var(--gold-dark)", letterSpacing: "0.1em" }}>
                            Voir le récapitulatif complet
                          </summary>
                          <div className="intake-html mt-3" data-testid="intake-recap">
                            <table className="intake-recap-table">
                              <tbody dangerouslySetInnerHTML={{ __html: intake.recap }} />
                            </table>
                          </div>
                        </details>
                      </section>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== OBJECTIVE TAB ===== */}
        {activeTab === "objective" && (
          <div data-testid="coach-insights-objective">
            <InvestmentObjectiveCard studentId={studentId} readOnly={isReadOnly} />
          </div>
        )}

        {/* ===== NOTES TAB ===== */}
        {activeTab === "notes" && (
          <div data-testid="coach-insights-notes">
            <textarea
              data-testid="coach-private-note-textarea"
              value={note}
              onChange={handleNoteChange}
              disabled={isReadOnly}
              placeholder={isReadOnly ? "Notes privées du coach (lecture seule)" : "Écris ici tes observations, idées de relance, ressenti sur l'élève… (visible uniquement par toi)"}
              className="input-dark w-full min-h-[180px] resize-y"
              style={{ fontFamily: "var(--serif)", fontStyle: "italic" }}
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs italic" style={{ color: "var(--muted)", fontFamily: "var(--serif)" }}>
                {noteSaving ? "Sauvegarde…" :
                  noteSavedAt ? `Sauvegardé le ${formatDate(noteSavedAt)}` :
                  "Sauvegarde automatique"}
              </p>
              <p className="text-xs uppercase tracking-wider" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>
                Confidentiel
              </p>
            </div>
          </div>
        )}

        {/* ===== SESSIONS TAB (Fathom recordings) ===== */}
        {activeTab === "sessions" && (
          <div data-testid="coach-insights-sessions">
            <CoachingSessionsView studentId={studentId} isCoachView={true} />
          </div>
        )}
      </div>

      <style>{`
        .intake-html {
          font-family: var(--serif), serif;
          font-size: 0.92rem;
          line-height: 1.65;
          color: var(--ink-soft);
        }
        .intake-html h1,
        .intake-html h2,
        .intake-html h3,
        .intake-html h4 {
          font-family: var(--serif), serif;
          color: var(--ink);
          font-weight: 700;
          margin: 14px 0 6px;
        }
        .intake-html h3 { font-size: 1.05rem; }
        .intake-html h4 { font-size: 0.95rem; color: var(--gold-dark); }
        .intake-html p { margin: 4px 0 10px; }
        .intake-html strong, .intake-html b { color: var(--ink); font-weight: 700; }
        .intake-html ul, .intake-html ol { padding-left: 1.4em; margin: 4px 0 10px; }
        .intake-html li { margin: 2px 0; }
        .intake-html a { color: var(--gold-dark); text-decoration: underline; }
        .intake-html blockquote {
          border-left: 3px solid var(--gold);
          padding: 4px 14px;
          margin: 10px 0;
          background: var(--paper-2);
          font-style: italic;
        }

        /* Recap table — rendered when Make.com sends <tr><td>...</tr> rows */
        .intake-recap-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
          font-family: 'Inter', sans-serif;
          font-size: 0.85rem;
        }
        .intake-recap-table tr {
          border-bottom: 1px solid var(--line);
        }
        .intake-recap-table tr:nth-child(even) {
          background: var(--paper-2);
        }
        .intake-recap-table tr:last-child {
          border-bottom: none;
        }
        .intake-recap-table td {
          padding: 8px 10px;
          vertical-align: top;
          color: var(--ink-soft);
          line-height: 1.5;
        }
        .intake-recap-table td:first-child {
          font-weight: 600;
          color: var(--ink);
          width: 38%;
          font-family: 'Inter', sans-serif;
        }
        .intake-recap-table td:last-child {
          font-family: var(--serif);
          font-style: italic;
        }
        /* Section headers inside recap (h2/h3/strong) */
        .intake-recap-table tr.section-header td,
        .intake-recap-table td[colspan] {
          background: var(--ink);
          color: #FFFFFF !important;
          font-family: var(--serif);
          font-weight: 700;
          font-size: 0.9rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          padding: 10px 12px;
        }
      `}</style>
    </div>
  );
}
